import React, { useState, useEffect } from 'react';
import { Download, Share } from 'lucide-react';

// Chrome/Edge/Android disparan beforeinstallprompt una sola vez, apenas carga la
// página: se captura al importar el módulo, antes de que monte el componente.
let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(deferredPrompt));

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        deferredPrompt = e;
        notify();
    });
    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        notify();
    });
}

const isStandalone = () =>
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

// iOS no tiene beforeinstallprompt: solo se instala desde Compartir > Agregar a inicio.
const isIOS = () =>
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

// Solo celulares/tablets: en escritorio Chrome ya ofrece instalar desde la barra de direcciones.
const isMobile = () => window.matchMedia?.('(pointer: coarse)').matches;

export default function InstallApp() {
    const [prompt, setPrompt] = useState(deferredPrompt);
    const [showIOSHelp, setShowIOSHelp] = useState(false);

    useEffect(() => {
        listeners.add(setPrompt);
        return () => listeners.delete(setPrompt);
    }, []);

    const ios = isIOS();
    if (isStandalone() || !isMobile() || (!prompt && !ios)) return null;

    const install = async () => {
        if (!prompt) {
            setShowIOSHelp((v) => !v);
            return;
        }
        prompt.prompt();
        await prompt.userChoice;
        // El evento sirve una sola vez; si lo rechaza, Chrome lo vuelve a disparar más adelante.
        deferredPrompt = null;
        notify();
    };

    return (
        <div className="flex flex-col items-end">
            <button
                type="button"
                onClick={install}
                className="px-3 py-2 rounded-xl bg-white border border-sky-100 text-sky-600 font-bold text-xs shadow-md flex items-center gap-1.5 active:scale-95 transition-all"
            >
                <Download size={16} /> Instalar esta app
            </button>
            {showIOSHelp && (
                <p role="status" className="mt-2 max-w-[220px] bg-white border border-sky-100 rounded-xl shadow-lg p-3 text-xs text-slate-600">
                    Toca <Share size={13} className="inline text-sky-600 align-text-bottom" /> <b>Compartir</b> y luego <b>Agregar a inicio</b>.
                </p>
            )}
        </div>
    );
}
